import type { DateRange } from '@/types';

export interface SanitizedLocationDates {
    dateRange: DateRange;
    pickupDate: Date | null;
    dateRangeCleared: boolean;
    pickupDateCleared: boolean;
}

export const startOfLocalDay = (date: Date = new Date()): Date => {
    const localDate = new Date(date);
    localDate.setHours(0, 0, 0, 0);
    return localDate;
};

export const isValidDate = (value: unknown): value is Date =>
    value instanceof Date && !Number.isNaN(value.getTime());

const isPastDate = (value: Date, now: Date): boolean =>
    startOfLocalDay(value).getTime() < startOfLocalDay(now).getTime();

export const sanitizeLocationDates = (
    dateRange: DateRange | undefined,
    pickupDate: Date | null | undefined,
    now: Date = new Date()
): SanitizedLocationDates => {
    const from = dateRange?.from;
    const to = dateRange?.to;

    const hasFrom = from !== undefined;
    const hasTo = to !== undefined;

    const fromInvalid = hasFrom && !isValidDate(from);
    const toInvalid = hasTo && !isValidDate(to);
    const fromPast = isValidDate(from) && isPastDate(from, now);
    const toPast = isValidDate(to) && isPastDate(to, now);
    const invertedRange = isValidDate(from) && isValidDate(to) && to.getTime() < from.getTime();

    const mustClearDateRange =
        fromInvalid ||
        toInvalid ||
        fromPast ||
        toPast ||
        invertedRange;

    const safePickupDate = isValidDate(pickupDate) ? pickupDate : null;
    const pickupDatePast = safePickupDate !== null && isPastDate(safePickupDate, now);
    const pickupDateCleared = (pickupDate !== null && pickupDate !== undefined) &&
        (!isValidDate(pickupDate) || pickupDatePast);

    return {
        dateRange: mustClearDateRange
            ? { from: undefined, to: undefined }
            : { from: isValidDate(from) ? from : undefined, to: isValidDate(to) ? to : undefined },
        pickupDate: pickupDateCleared ? null : safePickupDate,
        dateRangeCleared: mustClearDateRange,
        pickupDateCleared,
    };
};

const YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const parseDateParam = (value: string): Date => {
    if (YYYY_MM_DD_REGEX.test(value)) {
        // Local noon avoids timezone shifts while preserving the selected calendar date.
        return new Date(`${value}T12:00:00`);
    }

    return new Date(value);
};

